import React, { createContext, useContext, useState, useCallback } from "react";

type SettingsPage =
  | "positions"
  | "dress_codes"
  | "markets"
  | "billing_items"
  | "incident_types"
  | "abilities"
  | "restrictions"
  | "locations"
  | "unions";

interface SearchState {
  value: string;
  focused: boolean;
}

interface SettingsSearchContextType {
  getSearch: (page: SettingsPage) => SearchState;
  setSearchValue: (page: SettingsPage, value: string) => void;
  setSearchFocused: (page: SettingsPage, focused: boolean) => void;
}

const defaultState: SearchState = { value: "", focused: false };

const SettingsSearchContext = createContext<SettingsSearchContextType | undefined>(undefined);

export function SettingsSearchProvider({ children }: { children: React.ReactNode }) {
  const [searches, setSearches] = useState<Record<SettingsPage, SearchState>>({
    positions: { ...defaultState },
    dress_codes: { ...defaultState },
    markets: { ...defaultState },
    billing_items: { ...defaultState },
    incident_types: { ...defaultState },
    abilities: { ...defaultState },
    restrictions: { ...defaultState },
    locations: { ...defaultState },
    unions: { ...defaultState },
  });

  const getSearch = useCallback((page: SettingsPage) => searches[page], [searches]);

  const setSearchValue = useCallback((page: SettingsPage, value: string) => {
    setSearches((prev) => ({
      ...prev,
      [page]: { ...prev[page], value },
    }));
  }, []);

  const setSearchFocused = useCallback((page: SettingsPage, focused: boolean) => {
    setSearches((prev) => ({
      ...prev,
      [page]: { ...prev[page], focused },
    }));
  }, []);

  return (
    <SettingsSearchContext.Provider value={{ getSearch, setSearchValue, setSearchFocused }}>
      {children}
    </SettingsSearchContext.Provider>
  );
}

export function useSettingsSearch(page: SettingsPage) {
  const context = useContext(SettingsSearchContext);
  if (context === undefined) {
    throw new Error("useSettingsSearch must be used within a SettingsSearchProvider");
  }

  const { getSearch, setSearchValue, setSearchFocused } = context;
  const search = getSearch(page);

  return {
    searchValue: search.value,
    searchFocused: search.focused,
    setSearchValue: (value: string) => setSearchValue(page, value),
    setSearchFocused: (focused: boolean) => setSearchFocused(page, focused),
  };
}
